import { usePositionDetail } from 'apis/hooks'
import { DeactivatedStatusModal } from 'components/misc/deactivated-status'
import { BottomStickyBar } from 'components/common/bottom-sticky-bar'
import {
  Column,
  ResponsiveDisplay,
  ResponsiveLayout,
  Row,
  SpaceBetween,
} from 'components/common/layout'
import { Seo } from 'components/common/seo'
import { WithHeaderFooter } from 'components/misc/with-header-footer'
import { PositionButton } from 'components/positions/position-button'
import { PositionDetail } from 'components/positions/position-detail'
import { PositionHeader } from 'components/positions/position-header'
import { analytics } from 'infra/analytics'
import { HOST_BASE_URL } from 'infra/constants'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import React, { Suspense, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  updateStartupPositionInterested,
  viewStartupPosition,
} from 'utils/handlers'
import { useIsDesktop, usePositionIsInterestedUpdate } from 'utils/hooks'
import { PositionTable } from 'components/positions/position-table'
import { StartupSimpleBanner } from 'containers/positions/startup-simple-banner'
import { PositionAnalyzeButton } from 'components/positions/analysis/analyze-button'

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { positionId: _positionId } = context.query
  const positionId = Number(_positionId)
  if (!positionId) return { notFound: true } // positions/landing 경로가 어딘가 남아있어서 잘못된 접근을 방지시킴
  return { props: { positionId } }
}

const Position: React.FC<{ positionId: number }> = ({ positionId }) => {
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const { data: position, status } = usePositionDetail(positionId || undefined)
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false)
  const [viewedPositionId, setViewedPositionId] = useState<number | null>(null)
  usePositionIsInterestedUpdate(
    [positionId],
    `/startup-positions/${positionId}`,
  )

  useEffect(() => {
    // 비공개된 채용공고입니다 에러 시, 페이지에 남아있으면 재요청 보낼 수 있으므로 페이지 이동
    if (status === 404) router.replace('/404')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  useEffect(() => {
    // 유효한 채용공고 페이지인지 확인하고 view 처리할 수 있도록 함
    if (status !== 200 || positionId === viewedPositionId) return
    viewStartupPosition(positionId)
    setViewedPositionId(positionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, status])

  useEffect(() => {
    analytics.traffic('position_detail')
  }, [])

  if (!position) return null
  const infoParams = {
    startupId: position.startup.id,
    startupName: position.startup.name,
    positionId: position.id,
    positionName: position.name,
  }
  const save = () =>
    updateStartupPositionInterested(position.id, !position.isInterested)

  const positionAnalyzeBtn = (
    <Suspense>
      <PositionAnalyzeButton position={position} />
    </Suspense>
  )

  return (
    <>
      <Seo
        title={`${position.startup.name} - ${position.name} 채용 | 그룹바이`}
        description={position.startup.briefIntro}
        image={position.startup.thumbnail}
        url={`${HOST_BASE_URL}/positions/${positionId}`}
      />
      <WithHeaderFooter>
        <StartupSimpleBanner startup={position.startup} width={1060} />
        <ResponsiveLayout style={{ paddingTop: 60 }}>
          <PositionCard>
            <Column style={{ gap: 30, width: '100%' }}>
              <SpaceBetween style={{ gap: 24, alignItems: 'start' }}>
                <PositionHeader data={position} />
                <ResponsiveDisplay isDisableMobile>
                  {positionAnalyzeBtn}
                </ResponsiveDisplay>
              </SpaceBetween>
              <PositionTable
                data={{ ...position, hasCto: position.startup.hasCto }}
              />
              <ResponsiveDisplay isDisableDesktop>
                {positionAnalyzeBtn}
              </ResponsiveDisplay>
            </Column>
            <PositionDetail
              data={{
                ...position,
                location: position.location?.name || position.startup.location,
                address: position.address || position.startup.address,
              }}
              width={1060}
            />
          </PositionCard>
        </ResponsiveLayout>
        <BottomStickyBar>
          <BottomWrapper>
            <Row style={{ width: isDesktop ? 300 : '100%' }}>
              <PositionButton
                data={position}
                openDeactivatedModal={() => setIsDeactivatedModalOpen(true)}
                onSaveClick={() => {
                  save()
                  analytics.track('jobSearch_jsu_click_saveJobPost', infoParams)
                }}
                onApplyClick={() =>
                  analytics.track(
                    'jobSearch_jsu_click_applyJobPost',
                    infoParams,
                  )
                }
              />
            </Row>
          </BottomWrapper>
        </BottomStickyBar>
      </WithHeaderFooter>
      {isDeactivatedModalOpen && (
        <DeactivatedStatusModal
          onClose={() => setIsDeactivatedModalOpen(false)}
        />
      )}
    </>
  )
}

const BottomWrapper = styled(ResponsiveLayout)`
  gap: 12px;
  padding-top: 16px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: row;
  justify-content: end;
`

const PositionCard = styled(Column)`
  flex: 1;
  gap: 60px;
  margin-bottom: 80px;
  align-items: flex-start;
`

export default Position
